import { Badge, Switch } from "@/components/ui";
import { cn } from "@/lib/utils";
import { routes } from "@/routes/utils";
import useCurriculumMasterQuery from "@/services/masterData/curriculum/useGetCurriculumMasterQuery";
import useToogleCurriculumMasterMutation from "@/services/masterData/curriculum/useToogleCurriculumMasterMutation";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const useCurriculum = () => {
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const queryClient = useQueryClient();

  const queryParams = {
    ...filterData,
    ...(filterData?.curriculumTopic
      ? { curriculumTopic: filterData?.curriculumTopic }
      : {}),
    page: pageNumber + 1, // API expects 1-based page numbers
    orderBy: "createdAt",
    orderDirection: "DESC",
    limit: pageSize,
  };

  const {
    data: response,
    isPending,
    isError,
    error,
  } = useCurriculumMasterQuery(queryParams, {
    onError: (err) => {
      toast.error("Failed to fetch curriculum masters");
    },
  });
  
    const { mutate: toggleCurriculumMaster } = useToogleCurriculumMasterMutation({
    onSuccess: () => {
      toast.success("Curriculum Master status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["curriculumMaster"] });
    },
    onError: () => {
      toast.error("Failed to update status");
    },
  });

  const tableData = response?.data?.[0]?.records || [];
  const totalRecords = response?.data?.[0]?.pagination?.[0]?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "curriculumTopic",
      header: <div>Curriculum Topic</div>,
      cell: (row: any) => (
        <span
          className="text-primary font-normal cursor-pointer"
          onClick={()=>onRowClick(row)}
        >
          {String(row?.row?.original?.curriculumTopic || "")}
        </span>
      ),
      size: 120,
    },
    {
      accessorKey: "moduleName",
      header: <div>Module</div>,
      accessorFn: (row: any) => String(row?.module?.[0]?.module_name || ""),
      size: 100,
    },
    {
      accessorKey: "sub_module_name",
      header: <div>Sub Module</div>,
      accessorFn: (row: any) => String(row?.subModuleData?.[0]?.sub_module_name || ""),
      size: 100,
    },
    {
      accessorKey: "academicYear",
      header: <div>Academic Year</div>,
      accessorFn: (row: any) => String(row?.academicYear || ""),
      size: 80,
    },
    {
      accessorKey: "applicableGrades",
      header: <div>Applicable Grades</div>,
      cell: (row: any) => (
        <div className="flex flex-wrap gap-1">
          {(row?.row?.original?.gradesData || []).map(
            (grade: any, index: number) => (
              <span
                key={index}
                className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded"
              >
                {grade?.curriculum_grade}
              </span>
            )
          )}
        </div>
      ),
      size: 120,
    },
    {
      accessorKey: "activityType",
      header: <div>Activity Type</div>,
      cell: (row: any) => {
        const activityType = row?.row?.original?.activityType;
        return (
          <Badge
            variant="outline"
            className={cn(
              "rounded-[5px] px-4 py-2 border-none",
              activityType === "TLM"
                ? "bg-green-100 text-green-800"
                : activityType === "CONSUMABLES"
                ? "bg-orange-100 text-orange-800"
                : "bg-purple-100 text-purple-800"
            )}
          >
            {activityType}
          </Badge>
        );
      },
      size: 100,
    },
    {
      accessorKey: "createdAt",
      header: <div>Created On</div>,
      accessorFn: (row: any) =>
        moment(row?.createdAt).format("DD-MM-YYYY") || "",
      size: 80,
    },
    {
      accessorKey: "action",
      header: <div>Action</div>,
      size: 60,
      cell: (row: any) => (
        <Switch
          checked={row.row.original.isActive}
          className="cursor-pointer"
          onCheckedChange={() => {
            toggleCurriculumMaster({ id: row?.row?.original?._id });
           }}
        />
      )
    }
  ];

  const onAdd = () => {
    navigate(`${routes.curriculum}/${routes.add}`);
  };

  const onRowClick = (row: any) => {
    navigate(`${routes.curriculum}/${row?.row?.original._id}/${routes.edit}`);
  };

  const onCancelFilter = () => {
    setOpenFilter(false);
  };

  const onFilterBtnClick = () => {
    setOpenFilter(true);
  };

  return {
    columns,
    onAdd,
    onCancelFilter,
    onFilterBtnClick,
    openFilter,
    filterData,
    setFilterData,
    tableData,
    isPending,
    isError,
    pageNumber,
    pageSize,
    totalPages,
    totalRecords,
    setPageNumber,
    setPageSize,
  };
};

export default useCurriculum;
